import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  ShoppingCart as CartIcon,
  Business as BusinessIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import { ReorderSuggestion } from '../types';


interface CreatePurchaseOrderProps {
  open: boolean;
  onClose: () => void;
  suggestions: ReorderSuggestion[];
}

interface PurchaseOrderItem {
  sku: string;
  product: string;
  quantity: number;
  cost: number;
  supplier: string;
  leadTime: number;
  totalCost: number;
  selected: boolean;
}

interface PurchaseOrder {
  id: string;
  supplierId: string;
  supplierName: string;
  items: PurchaseOrderItem[];
  totalCost: number;
  expectedDelivery: Date;
  notes: string;
  status: 'draft' | 'sent' | 'confirmed' | 'shipped' | 'received';
}

const steps = [
  'Select Items',
  'Review & Split by Supplier',
  'Finalize Purchase Orders',
  'Send to Suppliers'
];

export const CreatePurchaseOrder: React.FC<CreatePurchaseOrderProps> = ({
  open,
  onClose,
  suggestions
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState<PurchaseOrderItem[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState<Date>(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)); // 14 days from now

  const initializeSelectedItems = useCallback(() => {
    const items: PurchaseOrderItem[] = suggestions.map(suggestion => ({
      sku: suggestion.SKU,
      product: suggestion.Product,
      quantity: suggestion.SuggestedQuantity,
      cost: suggestion.EstimatedCost,
      supplier: suggestion.SupplierName,
      leadTime: suggestion.LeadTime,
      totalCost: suggestion.EstimatedCost * suggestion.SuggestedQuantity,
      selected: true
    }));
    setSelectedItems(items);
  }, [suggestions]);

  useEffect(() => {
    if (open && suggestions.length > 0) {
      initializeSelectedItems();
    }
  }, [open, suggestions, initializeSelectedItems]);

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate that at least one item is selected
      if (selectedItems.filter(item => item.selected).length === 0) {
        setError('Please select at least one item to order');
        return;
      }
      createPurchaseOrders();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedItems([]);
    setPurchaseOrders([]);
    setNotes('');
    onClose();
  };

  const handleItemSelection = (sku: string, selected: boolean) => {
    setSelectedItems(prev => prev.map(item => 
      item.sku === sku ? { ...item, selected } : item
    ));
  };

  const handleQuantityChange = (sku: string, quantity: number) => {
    setSelectedItems(prev => prev.map(item => 
      item.sku === sku ? { ...item, quantity, totalCost: item.cost * quantity } : item
    ));
  };

  const createPurchaseOrders = () => {
    const selectedItemsList = selectedItems.filter(item => item.selected);
    
    // Group items by supplier
    const supplierGroups = selectedItemsList.reduce((groups, item) => {
      const supplier = item.supplier;
      if (!groups[supplier]) {
        groups[supplier] = [];
      }
      groups[supplier].push(item);
      return groups;
    }, {} as Record<string, PurchaseOrderItem[]>);

    // Create purchase orders for each supplier
    const orders: PurchaseOrder[] = Object.entries(supplierGroups).map(([supplierName, items]) => {
      const totalCost = items.reduce((sum, item) => sum + item.totalCost, 0);
      const maxLeadTime = Math.max(...items.map(item => item.leadTime));
      const deliveryDate = new Date(Date.now() + maxLeadTime * 24 * 60 * 60 * 1000);

      return {
        id: `PO-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        supplierId: `SUP-${supplierName.replace(/\s+/g, '-').toUpperCase()}`,
        supplierName,
        items,
        totalCost,
        expectedDelivery: deliveryDate,
        notes,
        status: 'draft'
      };
    });

    setPurchaseOrders(orders);
  };

  const sendPurchaseOrders = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, you would send POs to suppliers via API
      // For now, we'll simulate the process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update PO statuses
      setPurchaseOrders(prev => prev.map(po => ({ ...po, status: 'sent' as const })));
      
      setSuccess('Purchase orders sent successfully to all suppliers');
      setTimeout(() => setSuccess(null), 5000);
      
      // Close the dialog after a delay
      setTimeout(() => handleClose(), 3000);
    } catch (error) {
      console.error('Error sending purchase orders:', error);
      setError('Failed to send purchase orders');
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString();
  };

  const getTotalSelectedCost = () => {
    return selectedItems
      .filter(item => item.selected)
      .reduce((sum, item) => sum + item.totalCost, 0);
  };

  const getTotalSelectedItems = () => {
    return selectedItems.filter(item => item.selected).length;
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Create Purchase Orders from Reorder Suggestions
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 3 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Select Items */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Items to Order
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review and adjust the reorder suggestions before creating purchase orders.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Expected Delivery Date"
                    type="date"
                    value={expectedDelivery.toISOString().split('T')[0]}
                    onChange={(e) => setExpectedDelivery(new Date(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Notes"
                    placeholder="Add any special instructions for suppliers..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">
                      Reorder Suggestions ({getTotalSelectedItems()} selected)
                    </Typography>
                    <Typography variant="h6" color="primary">
                      Total: {formatCurrency(getTotalSelectedCost())}
                    </Typography>
                  </Box>

                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedItems.length > 0 && selectedItems.every(item => item.selected)}
                              indeterminate={selectedItems.some(item => item.selected) && !selectedItems.every(item => item.selected)}
                              onChange={(e) => setSelectedItems(prev => prev.map(item => ({ ...item, selected: e.target.checked })))}
                            />
                          </TableCell>
                          <TableCell>Product</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell align="right">Current Stock</TableCell>
                          <TableCell align="right">Reorder Point</TableCell>
                          <TableCell align="right">Suggested Qty</TableCell>
                          <TableCell align="right">Unit Cost</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                          <TableCell align="right">Lead Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedItems.map((item) => (
                          <TableRow key={item.sku} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={item.selected}
                                onChange={(e) => handleItemSelection(item.sku, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">{item.product}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{item.supplier}</TableCell>
                            <TableCell align="right">
                              {suggestions.find(s => s.SKU === item.sku)?.CurrentStock || 0}
                            </TableCell>
                            <TableCell align="right">
                              {suggestions.find(s => s.SKU === item.sku)?.ReorderPoint || 0}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(item.sku, Number(e.target.value))}
                                size="small"
                                sx={{ width: 80 }}
                                inputProps={{ min: 1 }}
                              />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(item.cost)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                            <TableCell align="right">{item.leadTime} days</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Step 2: Review & Split by Supplier */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Purchase Orders by Supplier
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Items have been automatically grouped by supplier. Review and adjust as needed.
              </Typography>

              {purchaseOrders.map((po, index) => (
                <Card key={po.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        {po.supplierName} - {po.supplierId}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={po.status.toUpperCase()} 
                          color={po.status === 'draft' ? 'default' : 'primary'}
                          size="small"
                        />
                        <Typography variant="h6" color="primary">
                          {formatCurrency(po.totalCost)}
                        </Typography>
                      </Box>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Unit Cost</TableCell>
                            <TableCell align="right">Total Cost</TableCell>
                            <TableCell align="right">Lead Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {po.items.map((item) => (
                            <TableRow key={item.sku}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">{item.product}</Typography>
                                  <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">{formatCurrency(item.cost)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                              <TableCell align="right">{item.leadTime} days</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Expected Delivery</Typography>
                          <Typography variant="body1">{formatDate(po.expectedDelivery)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Total Items</Typography>
                          <Typography variant="body1">{po.items.length}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Step 3: Finalize Purchase Orders */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Finalize Purchase Orders
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review the final purchase orders before sending to suppliers.
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Summary:</strong> {purchaseOrders.length} purchase orders will be created for{' '}
                  {purchaseOrders.length} suppliers, totaling {formatCurrency(purchaseOrders.reduce((sum, po) => sum + po.totalCost, 0))}.
                </Typography>
              </Alert>

              {purchaseOrders.map((po) => (
                <Card key={po.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {po.supplierName} - Purchase Order {po.id}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                      <Chip icon={<BusinessIcon />} label={`Supplier: ${po.supplierName}`} />
                      <Chip icon={<CartIcon />} label={`${po.items.length} items`} />
                      <Chip icon={<ShippingIcon />} label={`Delivery: ${formatDate(po.expectedDelivery)}`} />
                      <Chip label={formatCurrency(po.totalCost)} color="primary" />
                    </Box>

                    {notes && (
                      <Box sx={{ mb: 2, p: 2, backgroundColor: 'primary.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="primary">
                          <strong>Notes:</strong> {notes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Step 4: Send to Suppliers */}
          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Send Purchase Orders to Suppliers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Purchase orders are ready to be sent. This will email the POs to all suppliers and update their status.
              </Typography>

              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  All purchase orders have been prepared successfully. Click "Send to Suppliers" to finalize the process.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={sendPurchaseOrders}
                  disabled={loading}
                  sx={{ px: 4, py: 1.5 }}
                >
                  {loading ? 'Sending...' : 'Send to Suppliers'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 && (
            <Button onClick={handleNext} variant="contained">
              {activeStep === steps.length - 2 ? 'Review' : 'Next'}
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <Button onClick={handleNext} variant="contained" disabled>
              Complete
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Error and Success Messages */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">{success}</Alert>
      </Snackbar>
    </>
  );
};
